import React, { useState, useEffect, useRef } from 'react';
import MetaTags from '../../components/layout/MetaTags';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { 
  Sun, 
  Clock, 
  MoonStars, 
  X, 
  ArrowRight,
  ListChecks,
  CheckCircle,
  Star,
  VideoCamera,
  CaretLeft,
  CaretRight
} from '@phosphor-icons/react';
import './StudentLife.css';

// Import local images (keep these)
import salatSubuh from '../../assets/images/dailyActivities/masjid_agung.jpeg';
import ptq1 from '../../assets/images/dailyActivities/ptq1.jpeg';
import belajarDiKelas from '../../assets/images/dailyActivities/belajar_di_kelas.jpeg';

// Import all videos
import videoSantriBelajar from '../../assets/images/dailyActivities/video-webm/santri-belajar.webm';
import videoMuhadatsah from '../../assets/images/dailyActivities/video-webm/muhadatsah.webm';
import videoMakan from '../../assets/images/dailyActivities/video-webm/makan-di-dapur.webm';
import videoMasjid2 from '../../assets/images/dailyActivities/video-webm/masjid-2.webm';
import videoMasjidDrone from '../../assets/images/dailyActivities/video-webm/drone-masjid.webm';
import videoSolat from '../../assets/images/dailyActivities/video-webm/solat-di-masjid.webm';
import videoSilat from '../../assets/images/dailyActivities/video-webm/silat.webm';
import videoFutsal from '../../assets/images/dailyActivities/video-webm/futsal-1.webm';
import videoFutsal2 from '../../assets/images/dailyActivities/video-webm/futsal-2.webm';
import videoCloseUpBelajar from '../../assets/images/dailyActivities/video-webm/close-up-santri-belajar.webm';
import videoSantriDepanMasjid from '../../assets/images/dailyActivities/video-webm/santri-depan-masjid.webm';
import videoAreaBelajar from '../../assets/images/dailyActivities/video-webm/area-belajar.webm';
import videoAreaBelajar2 from '../../assets/images/dailyActivities/video-webm/area-belajar-2.webm';
import videoAsrama from '../../assets/images/dailyActivities/video-webm/asrama-1.webm';
import videoSedangMengaji from '../../assets/images/dailyActivities/video-webm/sedang-mengaji.webm';
import videoSuasanaBelajar from '../../assets/images/dailyActivities/video-webm/suasana-belajar.webm';
import videoRuangKelas from '../../assets/images/dailyActivities/video-webm/ruang-kelas.webm';
import videoRuangKelas2 from '../../assets/images/dailyActivities/video-webm/ruang-kelas-2.webm';
import videoLabIpa from '../../assets/images/dailyActivities/video-webm/lab-ipa-1.webm';
import videoLabKomputer from '../../assets/images/dailyActivities/video-webm/lab-komputer.webm';
import videoPerpus from '../../assets/images/dailyActivities/video-webm/perpus-1.webm';
import videoGerbang from '../../assets/images/dailyActivities/video-webm/gerbang-depan.webm';
import videoGSG from '../../assets/images/dailyActivities/video-webm/depan-gsg.webm';
import videoDrumband1 from '../../assets/images/dailyActivities/video-webm/drumband-1.webm';
import videoDrumband2 from '../../assets/images/dailyActivities/video-webm/drumband-2.webm';
import videoHadroh from '../../assets/images/dailyActivities/video-webm/hadroh-music.webm';
import videoLapFutsal from '../../assets/images/dailyActivities/video-webm/lap-futsal.webm';
import videoLapBola from '../../assets/images/dailyActivities/video-webm/lap-bola.webm';
import videoDepanKantin from '../../assets/images/dailyActivities/video-webm/depan-kantin.webm';
import videoDepanKelas from '../../assets/images/dailyActivities/video-webm/area-depan-kelas.webm';
import videoJalanPesantren from '../../assets/images/dailyActivities/video-webm/jalan-di-lingkungan-pesantren.webm';
import videoBersihBersih from '../../assets/images/dailyActivities/video-webm/bersih-bersih.webm';
import videoSantriShooting from '../../assets/images/dailyActivities/video-webm/santri-shooting.webm';

// Video Component
const VideoPlayer = ({ src, alt, className = '' }) => (
  <video 
    src={src}
    autoPlay
    muted
    loop
    playsInline
    className={`sl-video ${className}`}
    aria-label={alt}
  />
);

// Timeline data dengan multiple videos per item
const timelineData = [
  { time: "03:30", title: "Persiapan Pagi & Tadarus", image: ptq1, videos: [videoSedangMengaji, videoSantriBelajar], desc: "Membuka hari dengan Al-Qur'an dan persiapan ibadah", items: ["Bangun tidur", "Tadarus Al-Qur'an", "Persiapan shalat subuh"] },
  { time: "05:00", title: "Shalat Subuh", image: salatSubuh, videos: [videoSolat, videoMasjid2, videoMasjidDrone], desc: "Shalat berjamaah di masjid utama pesantren", items: ["Shalat Subuh berjamaah", "Dzikir pagi", "Doa bersama"] },
  { time: "05:40", title: "Bahasa & Tahfidz", image: null, videos: [videoMuhadatsah, videoCloseUpBelajar], desc: "Penguatan bahasa Arab dan hafalan Quran", items: ["Mufrodat harian", "Uslub & Muhadatsah", "Tahfidz takhossus"] },
  { time: "06:15", title: "Sarapan & Mandi", image: null, videos: [videoMakan, videoDepanKantin], desc: "Persiapan fisik sebelum kegiatan sekolah", items: ["Mandi", "Sarapan bergizi", "Persiapan perlengkapan"] },
  { time: "07:00", title: "Sekolah Formal", image: belajarDiKelas, videos: [videoSuasanaBelajar, videoRuangKelas, videoRuangKelas2, videoDepanKelas], desc: "Kegiatan belajar mengajar sesuai kurikulum", items: ["Pelajaran kelas", "Praktikum", "Konsultasi guru"] },
  { time: "12:00", title: "Shalat Zuhur", image: null, videos: [videoMasjid2, videoSantriDepanMasjid], desc: "Istirahat dan ibadah tengah hari", items: ["Shalat Zuhur", "Makan siang", "Istirahat"] },
  { time: "13:30", title: "Ekstrakurikuler", image: null, videos: [videoFutsal2, videoSilat, videoFutsal, videoLapFutsal, videoLapBola, videoSantriShooting], desc: "Pengembangan bakat dan minat santri", items: ["Olahraga", "Seni", "Keagamaan"] },
  { time: "15:30", title: "Tadarus Sore", image: null, videos: [videoCloseUpBelajar, videoAreaBelajar, videoAreaBelajar2], desc: "Muroja'ah dan setoran hafalan", items: ["Tadarus kelompok", "Setoran hafalan", "Koreksi bacaan"] },
  { time: "17:30", title: "Shalat Maghrib", image: null, videos: [videoMasjidDrone, videoMasjid2, videoSantriDepanMasjid], desc: "Ibadah senja berjamaah", items: ["Shalat Maghrib", "Dzikir petang", "Tafsir Al-Qur'an"] },
  { time: "19:30", title: "Shalat Isya", image: null, videos: [videoSantriDepanMasjid, videoMasjid2, videoMasjidDrone], desc: "Ibadah malam dan kajian", items: ["Shalat Isya berjamaah", "Kajian kitab", "Dzikir"] },
  { time: "20:30", title: "Belajar Malam", image: null, videos: [videoAreaBelajar, videoAreaBelajar2, videoSuasanaBelajar, videoPerpus], desc: "Menguatkan pelajaran dan bahasa", items: ["Ishlahul Lughah", "Muhadatsah", "PR & Muroja'ah"] },
  { time: "21:30", title: "Kebersihan Lingkungan", image: null, videos: [videoBersihBersih, videoJalanPesantren], desc: "Gotong royong menjaga kebersihan pesantren", items: ["Menyapu area asrama", "Membersihkan kelas", "Piket mandiri"] },
  { time: "22:00", title: "Istirahat", image: null, videos: [videoAsrama], desc: "Tidur malam untuk memulihkan energi", items: ["Persiapan tidur", "Tahajud (sunnah)", "Istirahat malam"] }
];

// Extracurriculars with videos
const extracurricularsWithVideos = [
  {
    title: "Tahfidz Al-Quran Takhossus",
    desc: "Program hafalan Al-Quran intensif",
    video: videoSedangMengaji,
    category: "Keagamaan"
  },
  {
    title: "Tilawah Al-Quran",
    desc: "Pelatihan membaca Al-Quran dengan tartil",
    video: videoCloseUpBelajar,
    category: "Keagamaan"
  },
  {
    title: "Pencak Silat",
    desc: "Latihan bela diri tradisional",
    video: videoSilat,
    category: "Olahraga"
  },
  {
    title: "Futsal",
    desc: "Tim futsal putra dan putri",
    video: videoFutsal,
    category: "Olahraga"
  },
  {
    title: "Sepak Bola",
    desc: "Latihan sepak bola lapangan",
    video: videoLapBola,
    category: "Olahraga"
  },
  {
    title: "Drumband",
    desc: "Marching band dan marching arts",
    videos: [videoDrumband1, videoDrumband2],
    category: "Seni"
  },
  {
    title: "Hadroh",
    desc: "Grup hadroh dan sholawat",
    video: videoHadroh,
    category: "Seni"
  },
  {
    title: "Praktikum IPA",
    desc: "Eksperimen sains dan laboratorium",
    video: videoLabIpa,
    category: "Akademik"
  },
  {
    title: "Komputer & IT",
    desc: "Pemrograman dan aplikasi komputer",
    video: videoLabKomputer,
    category: "Akademik"
  }
];

// Other extracurriculars without videos (text only)
const otherExtracurriculars = [
  "Bola Voli",
  "Bulu Tangkis", 
  "Tenis Meja",
  "Atletik",
  "PMR",
  "Pramuka",
  "Klub Bahasa",
  "Jurnalistik",
  "Karya Ilmiah",
  "Klub Matematika",
  "Klub Sains",
  "Panggung Gembira",
  "Paskibra",
  "ICTV"
];

// All Facilities with videos
const facilities = [
  {
    title: "Masjid Agung",
    desc: "Pusat ibadah dan kajian keislaman",
    video: videoMasjidDrone,
    category: "Ibadah"
  },
  {
    title: "Ruang Kelas",
    desc: "Fasilitas pembelajaran modern",
    video: videoRuangKelas,
    category: "Akademik"
  },
  {
    title: "Ruang Kelas 2",
    desc: "Lingkungan belajar yang nyaman",
    video: videoRuangKelas2,
    category: "Akademik"
  },
  {
    title: "Laboratorium IPA",
    desc: "Praktikum sains dan eksperimen",
    video: videoLabIpa,
    category: "Akademik"
  },
  {
    title: "Laboratorium Komputer",
    desc: "Pembelajaran teknologi dan IT",
    video: videoLabKomputer,
    category: "Akademik"
  },
  {
    title: "Perpustakaan",
    desc: "Koleksi kitab dan buku modern",
    video: videoPerpus,
    category: "Akademik"
  },
  {
    title: "Asrama Santri",
    desc: "Tempat tinggal yang nyaman",
    video: videoAsrama,
    category: "Hunian"
  },
  {
    title: "Area Belajar",
    desc: "Ruang belajar outdoor",
    video: videoAreaBelajar,
    category: "Akademik"
  },
  {
    title: "Taman Belajar",
    desc: "Area belajar terbuka",
    video: videoAreaBelajar2,
    category: "Akademik"
  },
  {
    title: "Lapangan Futsal",
    desc: "Fasilitas olahraga utama",
    video: videoLapFutsal,
    category: "Olahraga"
  },
  {
    title: "Lapangan Sepak Bola",
    desc: "Lapangan olahraga outdoor",
    video: videoLapBola,
    category: "Olahraga"
  },
  {
    title: "Gedung Serba Guna",
    desc: "Tempat kegiatan dan pertemuan",
    video: videoGSG,
    category: "Fasilitas Umum"
  },
  {
    title: "Kantin",
    desc: "Tempat makan dan istirahat",
    video: videoDepanKantin,
    category: "Fasilitas Umum"
  },
  {
    title: "Gerbang Utama",
    desc: "Pintu masuk utama pesantren",
    video: videoGerbang,
    category: "Fasilitas Umum"
  },
  {
    title: "Lingkungan Pesantren",
    desc: "Suasana asri dan kondusif",
    video: videoJalanPesantren,
    category: "Lingkungan"
  }
];

const StudentLife = () => {
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [visibleItems, setVisibleItems] = useState(new Set());
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [selectedExtracurricular, setSelectedExtracurricular] = useState(null);
  const [timelineVideoIndex, setTimelineVideoIndex] = useState(0);
  const [extraVideoIndex, setExtraVideoIndex] = useState(0);
  const timelineRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (timelineRef.current) {
        const rect = timelineRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        if (rect.top < windowHeight && rect.bottom > 0) {
          const height = rect.height;
          const scrolled = windowHeight - rect.top;
          const pct = Math.min(Math.max((scrolled / (height + windowHeight - 200)) * 100, 0), 100);
          setScrollProgress(pct);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const index = entry.target.dataset.index;
            setVisibleItems(prev => new Set([...prev, index]));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.timeline-row').forEach(el => observer.observe(el));
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const openModal = (activity) => {
    setSelectedActivity(activity);
    setTimelineVideoIndex(0);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedActivity(null);
    setTimelineVideoIndex(0);
    document.body.style.overflow = 'auto';
  };

  const nextTimelineVideo = () => {
    if (selectedActivity?.videos?.length > 1) {
      setTimelineVideoIndex((prev) => (prev + 1) % selectedActivity.videos.length);
    }
  };

  const prevTimelineVideo = () => {
    if (selectedActivity?.videos?.length > 1) {
      setTimelineVideoIndex((prev) => (prev - 1 + selectedActivity.videos.length) % selectedActivity.videos.length);
    }
  };

  const openFacilityModal = (facility) => {
    setSelectedFacility(facility);
    document.body.style.overflow = 'hidden';
  };

  const closeFacilityModal = () => {
    setSelectedFacility(null);
    document.body.style.overflow = 'auto';
  };

  const openExtracurricularModal = (item) => {
    setSelectedExtracurricular(item);
    setExtraVideoIndex(0);
    document.body.style.overflow = 'hidden';
  };

  const closeExtracurricularModal = () => {
    setSelectedExtracurricular(null);
    setExtraVideoIndex(0);
    document.body.style.overflow = 'auto';
  };

  const nextExtraVideo = () => {
    const videos = selectedExtracurricular?.videos;
    if (videos?.length > 1) {
      setExtraVideoIndex((prev) => (prev + 1) % videos.length);
    }
  };

  const prevExtraVideo = () => {
    const videos = selectedExtracurricular?.videos;
    if (videos?.length > 1) {
      setExtraVideoIndex((prev) => (prev - 1 + videos.length) % videos.length);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeModal();
        closeFacilityModal();
        closeExtracurricularModal();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Hero Images Animation - Add floating animation after entrance
  useEffect(() => {
    const heroImages = document.querySelectorAll('.sl-hero-img-wrap');
    
    // Add animated class after entrance animation completes
    const timer = setTimeout(() => {
      heroImages.forEach((img, index) => {
        setTimeout(() => {
          img.classList.add('animated');
        }, index * 150);
      });
    }, 1000);

    // Add tilt effect on mouse move
    const handleMouseMove = (e, img) => {
      const rect = img.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      
      const rotateX = (y - centerY) / 20;
      const rotateY = (centerX - x) / 20;
      
      img.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-10px) scale(1.03)`;
    };

    const handleMouseLeave = (img) => {
      img.style.transform = '';
    };

    heroImages.forEach(img => {
      img.addEventListener('mousemove', (e) => handleMouseMove(e, img));
      img.addEventListener('mouseleave', () => handleMouseLeave(img));
    });

    return () => {
      clearTimeout(timer);
      heroImages.forEach(img => {
        img.removeEventListener('mousemove', (e) => handleMouseMove(e, img));
        img.removeEventListener('mouseleave', () => handleMouseLeave(img));
      });
    };
  }, []);

  // Media Renderer Helper
  const renderMedia = (item, className = '') => {
    // Prioritize videos array (take first video), fallback to single video, then image
    if (item.videos && item.videos.length > 0) {
      return <VideoPlayer src={item.videos[0]} alt={item.title} className={className} />;
    }
    if (item.video) {
      return <VideoPlayer src={item.video} alt={item.title} className={className} />;
    }
    if (item.image) {
      return <img src={item.image} alt={item.title} className={className} loading="lazy" />;
    }
    return null;
  };

  return (
    <div className="student-life-page">
      <MetaTags
        title="Kehidupan Santri - Bahrul Ulum Islamic Center"
        description="Jelajahi rutinitas harian, kegiatan keagamaan, dan ekstrakurikuler yang membentuk karakter santri di Pondok Pesantren Bahrul Ulum."
      />
      <Header />

      {/* Hero Section */}
      <section className="sl-hero">
        <div className="sl-hero-container">
          <div className="sl-hero-grid">
            <div className={`sl-hero-content reveal ${visibleItems.has('hero') ? 'active' : ''}`} data-index="hero">
              <div className="sl-hero-badge">
                <Sun weight="fill" className="sl-hero-badge-icon sl-icon-sun" />
                Kehidupan Santri
              </div>
              <h1 className="sl-hero-title">
                Sehari dalam <br />
                <span className="sl-hero-title-accent">Kehidupan Pesantren</span>
              </h1>
              <p className="sl-hero-desc">
                Dari subuh hingga larut malam, setiap momen dirancang untuk membangun karakter, 
                menguatkan spiritualitas, dan mengasah intelektual santri.
              </p>
              <div className="sl-hero-stats">
                <div className="sl-hero-stat">
                  <Clock weight="fill" className="sl-hero-stat-icon sl-icon-clock" />
                  <span>15 Kegiatan Rutin</span>
                </div>
                <div className="sl-hero-stat">
                  <MoonStars weight="fill" className="sl-hero-stat-icon sl-icon-moon" />
                  <span>24 Jam Terstruktur</span>
                </div>
              </div>
            </div>
            
            <div className="sl-hero-images" style={{ transitionDelay: '0.2s' }}>
              <div className="sl-hero-img-col sl-hero-img-col-1">
                <div className="sl-hero-img-wrap">
                  <img src={ptq1} alt="Santri" loading="lazy" />
                </div>
                <div className="sl-hero-img-wrap sl-hero-video-wrap">
                  <VideoPlayer src={videoSantriBelajar} alt="Santri Belajar" />
                </div>
              </div>
              <div className="sl-hero-img-col sl-hero-img-col-2">
                <div className="sl-hero-img-wrap sl-hero-video-wrap">
                  <VideoPlayer src={videoMuhadatsah} alt="Muhadatsah" />
                </div>
                <div className="sl-hero-img-wrap">
                  <img src={belajarDiKelas} alt="Kelas" loading="lazy" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="sl-timeline-section" ref={timelineRef}>
        <div className="sl-timeline-container">
          <div className="sl-section-header reveal">
            <h2 className="sl-section-title">Jadwal Harian</h2>
            <p className="sl-section-subtitle">Rutinitas yang membentuk disiplin dan keberkahan</p>
          </div>

          <div className="sl-timeline-wrapper">
            <div className="sl-timeline-track"></div>
            <div className="sl-timeline-progress" style={{ height: `${scrollProgress}%` }}></div>
            
            <div className="sl-timeline-content">
              {timelineData.map((item, index) => {
                const isLeft = index % 2 === 0;
                return (
                  <div 
                    key={index} 
                    className={`sl-timeline-row reveal ${visibleItems.has(String(index)) ? 'active' : ''}`}
                    data-index={index}
                  >
                    {isLeft ? (
                      <>
                        <div className="sl-timeline-left">
                          <div className="sl-routine-card" onClick={() => openModal(item)}>
                            <div className="sl-card-image-wrap">
                              {renderMedia(item, 'sl-card-image')}
                              <div className="sl-time-badge">
                                <Clock weight="fill" />
                                {item.time} WIB
                              </div>
                            </div>
                            <div className="sl-card-content">
                              <h3 className="sl-card-title">{item.title}</h3>
                              <p className="sl-card-desc">{item.desc}</p>
                              <div className="sl-card-arrow">
                                <ArrowRight weight="bold" />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="sl-timeline-node" onClick={() => openModal(item)}>
                          {String(index + 1).padStart(2, '0')}
                        </div>
                        <div className="sl-timeline-right sl-hidden-mobile"></div>
                      </>
                    ) : (
                      <>
                        <div className="sl-timeline-left sl-hidden-mobile"></div>
                        <div className="sl-timeline-node" onClick={() => openModal(item)}>
                          {String(index + 1).padStart(2, '0')}
                        </div>
                        <div className="sl-timeline-right">
                          <div className="sl-routine-card" onClick={() => openModal(item)}>
                            <div className="sl-card-image-wrap">
                              {renderMedia(item, 'sl-card-image')}
                              <div className="sl-time-badge">
                                <Clock weight="fill" />
                                {item.time} WIB
                              </div>
                            </div>
                            <div className="sl-card-content">
                              <h3 className="sl-card-title">{item.title}</h3>
                              <p className="sl-card-desc">{item.desc}</p>
                              <div className="sl-card-arrow">
                                <ArrowRight weight="bold" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Extracurriculars Section - Grid Layout with Videos */}
      <section className="sl-extracurriculars-section">
        <div className="sl-extracurriculars-container">
          <div className="sl-section-header reveal">
            <h2 className="sl-section-title">Ekstrakurikuler</h2>
            <p className="sl-section-subtitle">Pengembangan bakat dan minat santri</p>
          </div>

          {/* Featured Extracurriculars with Videos */}
          <div className="sl-extracurriculars-grid">
            {extracurricularsWithVideos.map((item, index) => (
              <div 
                key={index} 
                className="sl-extracurricular-card reveal"
                style={{ transitionDelay: `${index * 0.05}s` }}
                onClick={() => openExtracurricularModal(item)}
              >
                <div className="sl-extracurricular-image-wrap">
                  <VideoPlayer src={item.videos?.[0] || item.video} alt={item.title} className="sl-extracurricular-video" />
                  <div className="sl-extracurricular-overlay"></div>
                  <div className="sl-extracurricular-category">{item.category}</div>
                  <div className="sl-extracurricular-hover">
                    <span>Lihat Detail</span>
                  </div>
                </div>
                <div className="sl-extracurricular-content">
                  <h3 className="sl-extracurricular-title">{item.title}</h3>
                  <p className="sl-extracurricular-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Other Extracurriculars - Tags */}
          <div className="sl-extracurriculars-others reveal">
            <h3 className="sl-extracurriculars-others-title">
              <ListChecks weight="fill" />
              Ekstrakurikuler Lainnya
            </h3>
            <div className="sl-extracurriculars-tags">
              {otherExtracurriculars.map((item, i) => (
                <span key={i} className="sl-extracurricular-tag">
                  <Star weight="fill" />
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Facilities Section */}
      <section className="sl-facilities-section">
        <div className="sl-facilities-container">
          <div className="sl-section-header reveal">
            <h2 className="sl-section-title">Fasilitas Lengkap</h2>
            <p className="sl-section-subtitle">Sarana penunjang kegiatan santri yang nyaman dan modern</p>
          </div>

          <div className="sl-facilities-grid sl-facilities-grid-large">
            {facilities.map((facility, index) => (
              <div 
                key={index} 
                className="sl-facility-card reveal" 
                style={{ transitionDelay: `${index * 0.05}s` }}
                onClick={() => openFacilityModal(facility)}
              >
                <div className="sl-facility-image-wrap">
                  <VideoPlayer src={facility.video} alt={facility.title} className="sl-facility-image" />
                  <div className="sl-facility-overlay"></div>
                  <div className="sl-facility-category">{facility.category}</div>
                  <div className="sl-facility-hover-text">
                    <span>Lihat Detail</span>
                  </div>
                </div>
                <div className="sl-facility-content">
                  <h3 className="sl-facility-title">{facility.title}</h3>
                  <p className="sl-facility-desc">{facility.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Activity Modal with Video Gallery */}
      {selectedActivity && (
        <div className="sl-modal-overlay" onClick={closeModal}>
          <div className="sl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sl-modal-image-wrap">
              {/* Video Gallery */}
              {selectedActivity.videos && selectedActivity.videos.length > 0 ? (
                <>
                  <VideoPlayer 
                    src={selectedActivity.videos[timelineVideoIndex]} 
                    alt={`${selectedActivity.title} - Video ${timelineVideoIndex + 1}`} 
                    className="sl-modal-image" 
                  />
                  {/* Video Navigation */}
                  {selectedActivity.videos.length > 1 && (
                    <>
                      <button className="sl-video-nav sl-video-prev" onClick={(e) => { e.stopPropagation(); prevTimelineVideo(); }}>
                        <CaretLeft weight="bold" />
                      </button>
                      <button className="sl-video-nav sl-video-next" onClick={(e) => { e.stopPropagation(); nextTimelineVideo(); }}>
                        <CaretRight weight="bold" />
                      </button>
                      {/* Video Indicators */}
                      <div className="sl-video-indicators">
                        {selectedActivity.videos.map((_, idx) => (
                          <button
                            key={idx}
                            className={`sl-video-dot ${idx === timelineVideoIndex ? 'active' : ''}`}
                            onClick={(e) => { e.stopPropagation(); setTimelineVideoIndex(idx); }}
                          />
                        ))}
                      </div>
                      {/* Video Counter */}
                      <div className="sl-video-counter">
                        <VideoCamera weight="fill" />
                        <span>{timelineVideoIndex + 1} / {selectedActivity.videos.length}</span>
                      </div>
                    </>
                  )}
                </>
              ) : selectedActivity.image ? (
                <img src={selectedActivity.image} alt={selectedActivity.title} className="sl-modal-image" />
              ) : null}
              <div className="sl-modal-image-overlay"></div>
              <button className="sl-modal-close" onClick={closeModal}>
                <X weight="bold" />
              </button>
              <div className="sl-modal-header">
                <span className="sl-modal-time">{selectedActivity.time} WIB</span>
                <h2 className="sl-modal-title">{selectedActivity.title}</h2>
              </div>
            </div>
            <div className="sl-modal-body">
              <p className="sl-modal-facility-desc">{selectedActivity.desc}</p>
              <h3 className="sl-modal-section-title">
                <div className="sl-modal-icon">
                  <CheckCircle weight="fill" />
                </div>
                Aktivitas yang Dilakukan:
              </h3>
              <div className="sl-modal-items">
                {selectedActivity.items.map((item, i) => (
                  <div key={i} className="sl-modal-item" style={{ animationDelay: `${i * 0.1}s` }}>
                    <div className="sl-modal-item-number">{i + 1}</div>
                    <span className="sl-modal-item-text">{item}</span>
                  </div>
                ))}
              </div>
              <button className="sl-modal-btn" onClick={closeModal}>
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extracurricular Modal */}
      {selectedExtracurricular && (
        <div className="sl-modal-overlay" onClick={closeExtracurricularModal}>
          <div className="sl-modal sl-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="sl-modal-image-wrap sl-modal-image-wrap-large">
              {/* Video Gallery */}
              {selectedExtracurricular.videos && selectedExtracurricular.videos.length > 0 ? (
                <>
                  <VideoPlayer 
                    src={selectedExtracurricular.videos[extraVideoIndex]} 
                    alt={`${selectedExtracurricular.title} - Video ${extraVideoIndex + 1}`} 
                    className="sl-modal-image" 
                  />
                  {/* Video Navigation */}
                  {selectedExtracurricular.videos.length > 1 && (
                    <>
                      <button className="sl-video-nav sl-video-prev" onClick={(e) => { e.stopPropagation(); prevExtraVideo(); }}>
                        <CaretLeft weight="bold" />
                      </button>
                      <button className="sl-video-nav sl-video-next" onClick={(e) => { e.stopPropagation(); nextExtraVideo(); }}>
                        <CaretRight weight="bold" />
                      </button>
                      <div className="sl-video-indicators">
                        {selectedExtracurricular.videos.map((_, idx) => (
                          <button
                            key={idx}
                            className={`sl-video-dot ${idx === extraVideoIndex ? 'active' : ''}`}
                            onClick={(e) => { e.stopPropagation(); setExtraVideoIndex(idx); }}
                          />
                        ))}
                      </div>
                      <div className="sl-video-counter">
                        <VideoCamera weight="fill" />
                        <span>{extraVideoIndex + 1} / {selectedExtracurricular.videos.length}</span>
                      </div>
                    </>
                  )}
                </>
              ) : selectedExtracurricular.video ? (
                <VideoPlayer src={selectedExtracurricular.video} alt={selectedExtracurricular.title} className="sl-modal-image" />
              ) : null}
              <div className="sl-modal-image-overlay"></div>
              <button className="sl-modal-close" onClick={closeExtracurricularModal}>
                <X weight="bold" />
              </button>
              <div className="sl-modal-header">
                <span className="sl-modal-category">{selectedExtracurricular.category}</span>
                <h2 className="sl-modal-title">{selectedExtracurricular.title}</h2>
              </div>
            </div>
            <div className="sl-modal-body">
              <p className="sl-modal-facility-desc">{selectedExtracurricular.desc}</p>
              <button className="sl-modal-btn" onClick={closeExtracurricularModal}>
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Facility Modal */}
      {selectedFacility && (
        <div className="sl-modal-overlay" onClick={closeFacilityModal}>
          <div className="sl-modal sl-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="sl-modal-image-wrap sl-modal-image-wrap-large">
              <VideoPlayer src={selectedFacility.video} alt={selectedFacility.title} className="sl-modal-image" />
              <div className="sl-modal-image-overlay"></div>
              <button className="sl-modal-close" onClick={closeFacilityModal}>
                <X weight="bold" />
              </button>
              <div className="sl-modal-header">
                <span className="sl-modal-category">{selectedFacility.category}</span>
                <h2 className="sl-modal-title">{selectedFacility.title}</h2>
              </div>
            </div>
            <div className="sl-modal-body">
              <p className="sl-modal-facility-desc">{selectedFacility.desc}</p>
              <button className="sl-modal-btn" onClick={closeFacilityModal}>
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default StudentLife;
